import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

export default function SettingsGoogleOAuthPage() {
  const { profile } = useAuth();
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      if (!profile?.company_id) return;
      const { data } = await supabase
        .from('whatsapp_configs')
        .select('settings')
        .eq('company_id', profile.company_id)
        .maybeSingle();
      if (data?.settings && typeof data.settings === 'object') {
        const s = data.settings as Record<string, unknown>;
        if (typeof s.google_oauth_client_id === 'string') setClientId(s.google_oauth_client_id);
        if (typeof s.google_oauth_client_secret === 'string') setClientSecret(s.google_oauth_client_secret);
      }
      setLoaded(true);
    };
    loadSettings();
  }, [profile?.company_id]);

  const handleSave = async () => {
    if (!profile?.company_id) return;
    setSaving(true);
    setMessage(null);
    try {
      const { data: existing } = await supabase
        .from('whatsapp_configs')
        .select('id, settings')
        .eq('company_id', profile.company_id)
        .maybeSingle();

      const currentSettings = (existing?.settings && typeof existing.settings === 'object') ? (existing.settings as Record<string, unknown>) : {};
      const newSettings = {
        ...currentSettings,
        google_oauth_client_id: clientId,
        google_oauth_client_secret: clientSecret,
      };

      if (existing?.id) {
        await supabase.from('whatsapp_configs').update({ settings: newSettings, updated_at: new Date().toISOString() }).eq('id', existing.id);
      } else {
        await supabase.from('whatsapp_configs').insert({ company_id: profile.company_id, settings: newSettings });
      }
      setMessage({ type: 'success', text: 'Configurações do Google OAuth salvas com sucesso!' });
    } catch (err: unknown) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Erro ao salvar' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const isConfigured = !!(clientId && clientSecret);

  if (!loaded) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-heading font-bold text-foreground-950">Google OAuth</h2>
        <p className="text-sm text-foreground-500 mt-1">Configure login social com Google para seus clientes</p>
      </div>

      {message && (
        <div className={`rounded-xl px-4 py-3 flex items-center gap-3 ${message.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          <i className={`${message.type === 'success' ? 'ri-check-line text-emerald-500' : 'ri-error-warning-line text-red-500'} text-lg`} />
          <p className="text-sm">{message.text}</p>
        </div>
      )}

      <div className="bg-background-100 rounded-2xl border border-background-200 p-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Client ID</label>
            <input type="text" value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="123456789-xxxxx.apps.googleusercontent.com" className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Client Secret</label>
            <input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="GOCSPX-..." className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-all" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Redirect URI (cole no Google Cloud Console)</label>
            <input type="text" readOnly value={`${window.location.origin}/auth/google-callback`} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-500 outline-none cursor-text" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Status</label>
            <div className={`flex items-center gap-2 px-3 py-2 border rounded-lg ${isConfigured ? 'bg-emerald-50 border-emerald-200' : 'bg-background-100 border-background-200'}`}>
              <span className={`w-2 h-2 rounded-full ${isConfigured ? 'bg-emerald-500' : 'bg-foreground-300'}`} />
              <span className={`text-sm font-medium ${isConfigured ? 'text-emerald-700' : 'text-foreground-500'}`}>{isConfigured ? 'Configurado' : 'Não configurado'}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 pt-3 border-t border-background-100">
          <button onClick={handleSave} disabled={saving} className="px-5 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
            {saving ? <><i className="ri-loader-4-line animate-spin" /> Salvando...</> : <><i className="ri-save-line" /> Salvar</>}
          </button>
          <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="nofollow" className="px-5 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2 no-underline">
            <i className="ri-external-link-line" /> Console Google Cloud
          </a>
        </div>
      </div>
    </div>
  );
}